/**
 * Detección de plataforma y modo de presentación (T017; implementation-notes 002 §4).
 *
 * `detectPlatform()` → `DevicePlatform` (`ios | android | desktop | other`) y `displayMode()` →
 * `DisplayMode` (`standalone | browser`), ambos enums de `@fermin/contracts`, van en
 * `DeviceEnrollBody`. `isIos()` decide la bifurcación del flujo de activación (iOS necesita la PWA
 * instalada + código de traspaso, research R-5); `isStandalone()` detecta si ya se abrió instalada.
 *
 * Las funciones aceptan el entorno como parámetro para poder probarlas sin tocar `navigator`.
 */
import { UAParser } from 'ua-parser-js';

import { DevicePlatform, DisplayMode, USER_AGENT_MAX_LENGTH } from '@fermin/contracts';

/** Datos del navegador relevantes para la detección. */
export interface PlatformEnv {
  userAgent: string;
  /** `navigator.userAgentData.platform` (Chromium; `undefined` en Safari/Firefox). */
  uaDataPlatform?: string;
  /** `navigator.platform` (`MacIntel` en iPadOS 13+, que se anuncia como Mac). */
  platform?: string;
  maxTouchPoints?: number;
}

interface NavigatorWithUAData extends Navigator {
  userAgentData?: { platform?: string };
}

/** Lee el entorno del `navigator` actual (vacío fuera del navegador). */
export function browserEnv(): PlatformEnv {
  if (typeof navigator === 'undefined') {
    return { userAgent: '' };
  }
  const nav = navigator as NavigatorWithUAData;
  return {
    userAgent: nav.userAgent ?? '',
    uaDataPlatform: nav.userAgentData?.platform,
    platform: nav.platform,
    maxTouchPoints: nav.maxTouchPoints,
  };
}

const DESKTOP_OS =
  /^(windows|mac ?os|linux|chrome ?os|chromium ?os|ubuntu|debian|fedora|freebsd|openbsd)/i;

/**
 * iPadOS 13+ se anuncia como Safari de macOS; la pista es `MacIntel` con pantalla táctil.
 * Los Mac con Safari reportan `maxTouchPoints === 0`.
 */
function isIpadOsAsMac(env: PlatformEnv): boolean {
  return (
    env.platform === 'MacIntel' && (env.maxTouchPoints ?? 0) > 1 && /Safari/i.test(env.userAgent)
  );
}

/** `true` en iPhone, iPad (incluido iPadOS anunciado como Mac) e iPod touch. */
export function isIos(env: PlatformEnv = browserEnv()): boolean {
  if (/iPhone|iPad|iPod/i.test(env.userAgent)) {
    return true;
  }
  if (env.uaDataPlatform !== undefined && /^iOS$/i.test(env.uaDataPlatform)) {
    return true;
  }
  return isIpadOsAsMac(env);
}

/** `ios | android | desktop | other` para `DeviceEnrollBody.platform`. */
export function detectPlatform(env: PlatformEnv = browserEnv()): DevicePlatform {
  if (isIos(env)) {
    return DevicePlatform.enum.ios;
  }

  const result = UAParser(env.userAgent);
  const osName = result.os.name ?? '';

  if (/^android$/i.test(osName) || /Android/i.test(env.userAgent)) {
    return DevicePlatform.enum.android;
  }
  if (/^ios$/i.test(osName)) {
    return DevicePlatform.enum.ios;
  }

  // Client hints (Chromium): más fiables que el UA reducido.
  const hinted = env.uaDataPlatform ?? '';
  if (/^android$/i.test(hinted)) {
    return DevicePlatform.enum.android;
  }
  if (DESKTOP_OS.test(hinted)) {
    return DevicePlatform.enum.desktop;
  }

  const deviceType = result.device.type;
  if (deviceType === undefined && DESKTOP_OS.test(osName)) {
    return DevicePlatform.enum.desktop;
  }
  if (deviceType === undefined && osName !== '' && !/Mobile/i.test(env.userAgent)) {
    return DevicePlatform.enum.desktop;
  }
  return DevicePlatform.enum.other;
}

interface NavigatorWithStandalone extends Navigator {
  /** Safari iOS: `true` cuando la PWA se abrió desde la pantalla de inicio. */
  standalone?: boolean;
}

/** `true` si la PWA corre instalada (display-mode standalone o `navigator.standalone` en iOS). */
export function isStandalone(win: Window | undefined = globalThis.window): boolean {
  if (!win) {
    return false;
  }
  try {
    if (
      typeof win.matchMedia === 'function' &&
      win.matchMedia('(display-mode: standalone)').matches
    ) {
      return true;
    }
  } catch {
    // matchMedia ausente o roto: se cae al indicador de Safari.
  }
  return (win.navigator as NavigatorWithStandalone | undefined)?.standalone === true;
}

/** `standalone | browser` para `DeviceEnrollBody.display_mode`. */
export function displayMode(win: Window | undefined = globalThis.window): DisplayMode {
  return isStandalone(win) ? DisplayMode.enum.standalone : DisplayMode.enum.browser;
}

/** `navigator.userAgent` recortado al máximo del contrato (`UserAgentSchema`). */
export function userAgent(env: PlatformEnv = browserEnv()): string {
  return env.userAgent.slice(0, USER_AGENT_MAX_LENGTH);
}

// ---------------------------------------------------------------------------
// User-Agent Client Hints (005 · QA-5)
// ---------------------------------------------------------------------------

/** Plazo máximo para leer los client hints: el registro nunca espera más que esto por ellos. */
export const CLIENT_HINTS_TIMEOUT_MS = 800;

interface HighEntropyUAData {
  getHighEntropyValues?: (
    hints: string[],
  ) => Promise<{ model?: unknown; platformVersion?: unknown; platform?: unknown }>;
}

/**
 * Plataformas cuyos hints se envían. En escritorio el servidor no los usa, y un navegador de
 * escritorio que se hace pasar por Android (modo "sitio móvil", emulación) daría la versión del
 * sistema anfitrión: si `platform` dice otra cosa, se descartan.
 */
const HINT_PLATFORMS = new Set(['Android', 'HarmonyOS']);

/** Lo que va en `DeviceEnrollBody.client_hints` (mismo tipo que `DeviceClientHints`). */
export interface ClientHints {
  model?: string;
  platform_version?: string;
}

function hintString(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed.slice(0, max);
}

/**
 * `navigator.userAgentData.getHighEntropyValues(['model', 'platformVersion'])` (solo Chromium).
 * En Android es la única fuente del modelo y de la versión real del sistema (Chrome reduce el
 * user-agent a `Android 10; K`). Devuelve `undefined` si la API no existe (Safari, Firefox), si
 * falla o si tarda más de `timeoutMs`: **nunca** bloquea ni rompe el registro.
 */
export async function clientHints(
  nav: Navigator | undefined = typeof navigator === 'undefined' ? undefined : navigator,
  timeoutMs: number = CLIENT_HINTS_TIMEOUT_MS,
): Promise<ClientHints | undefined> {
  const uaData = (nav as (Navigator & { userAgentData?: HighEntropyUAData }) | undefined)
    ?.userAgentData;
  if (typeof uaData?.getHighEntropyValues !== 'function') {
    return undefined;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const values = await Promise.race([
      uaData.getHighEntropyValues(['model', 'platformVersion']),
      new Promise<undefined>((resolveTimeout) => {
        timer = setTimeout(() => resolveTimeout(undefined), timeoutMs);
      }),
    ]);
    if (!values) {
      return undefined;
    }
    if (typeof values.platform === 'string' && !HINT_PLATFORMS.has(values.platform)) {
      return undefined;
    }
    const hints: ClientHints = {};
    const model = hintString(values.model, 64);
    const platformVersion = hintString(values.platformVersion, 32);
    if (model !== undefined) {
      hints.model = model;
    }
    if (platformVersion !== undefined) {
      hints.platform_version = platformVersion;
    }
    return Object.keys(hints).length === 0 ? undefined : hints;
  } catch {
    return undefined;
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }
}
